import { useEffect, useState, useCallback, useRef } from "react";
import api from "../api/api";
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Typography, IconButton, TextField, MenuItem,
  TablePagination, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, CircularProgress, Alert, Tabs, Tab,
  InputAdornment, List, ListItem, ListItemText, ListItemSecondaryAction,
  Divider, Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import ArticleIcon from "@mui/icons-material/Article";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CursoForm from "../components/CursoForm";
import ConfirmDialog from "../components/ConfirmDialog";
import { useAuth } from "../components/AuthContext";

const baseURL = process.env.REACT_APP_API_URL || "";

const TIPO_LABELS = { general: "General", puesto: "By Position", opcional: "Optional" };
const TIPO_COLORS = { general: "primary", puesto: "secondary", opcional: "default" };

export default function Cursos() {
  const { hasPermission } = useAuth();
  const [cursos, setCursos] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [busqueda, setBusqueda] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroPuesto, setFiltroPuesto] = useState("");
  const [puestos, setPuestos] = useState([]);

  const [openForm, setOpenForm] = useState(false);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCurso, setDialogCurso] = useState(null);
  const [tab, setTab] = useState(0);
  const [pendientes, setPendientes] = useState([]);
  const [completados, setCompletados] = useState([]);
  const [loadingEmpleados, setLoadingEmpleados] = useState(false);
  const [errorEmpleados, setErrorEmpleados] = useState("");

  // — Working sheets —
  const [fichasOpen, setFichasOpen] = useState(false);
  const [fichasCurso, setFichasCurso] = useState(null);
  const [fichasAsignadas, setFichasAsignadas] = useState([]);
  const [fichasBusqueda, setFichasBusqueda] = useState("");
  const [fichasResultados, setFichasResultados] = useState([]);
  const [loadingFichas, setLoadingFichas] = useState(false);
  const [loadingBusqueda, setLoadingBusqueda] = useState(false);
  const fichasDebounceRef = useRef(null);

  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/puestos").then(r => setPuestos(r.data)).catch(() => {});
  }, []);

  const cargarCursos = useCallback(async (q, tipo, idPuesto) => {
    try {
      const params = {};
      if (q) params.q = q;
      if (tipo) params.tipo = tipo;
      if (idPuesto) params.id_puesto = idPuesto;
      const res = await api.get("/cursos", { params });
      setCursos(res.data);
      setPage(0);
    } catch (err) {
      console.error(err);
      alert("You do not have permission to view courses.");
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      cargarCursos(busqueda, filtroTipo, filtroPuesto);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [busqueda, filtroTipo, filtroPuesto, cargarCursos]);

  const verPersonas = async (curso) => {
    setDialogCurso(curso);
    setPendientes([]);
    setCompletados([]);
    setErrorEmpleados("");
    setTab(0);
    setDialogOpen(true);
    setLoadingEmpleados(true);
    try {
      const [resPend, resComp] = await Promise.all([
        api.get(`/cursos/${curso.id_curso}/empleados`),
        api.get(`/cursos/${curso.id_curso}/empleados_completados`),
      ]);
      setPendientes(resPend.data);
      setCompletados(resComp.data);
    } catch (e) {
      const msg = e?.response?.data?.detail ?? e?.message ?? "Error loading employees";
      setErrorEmpleados(msg);
    } finally {
      setLoadingEmpleados(false);
    }
  };

  const eliminarCurso = async () => {
    try {
      await api.delete(`/cursos/${deleteId}`);
      setConfirmOpen(false);
      cargarCursos(busqueda, filtroTipo, filtroPuesto);
    } catch {
      alert("Cannot delete: the course is in use.");
    }
  };

  const cursosPaginados = cursos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // — Working sheets: open dialog and load assigned ones —
  const abrirFichas = async (curso) => {
    setFichasCurso(curso);
    setFichasAsignadas([]);
    setFichasResultados([]);
    setFichasBusqueda("");
    setFichasOpen(true);
    setLoadingFichas(true);
    try {
      const res = await api.get(`/fichas/cursos/${curso.id_curso}`);
      setFichasAsignadas(res.data);
    } catch (e) {
      alert("Error loading working sheets: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingFichas(false);
    }
  };

  // — Working sheets: debounced search —
  const handleFichasBusqueda = (valor) => {
    setFichasBusqueda(valor);
    clearTimeout(fichasDebounceRef.current);
    if (valor.trim().length < 2) { setFichasResultados([]); return; }
    fichasDebounceRef.current = setTimeout(async () => {
      setLoadingBusqueda(true);
      try {
        const res = await api.get("/fichas", { params: { q: valor } });
        setFichasResultados(res.data);
      } catch {
        setFichasResultados([]);
      } finally {
        setLoadingBusqueda(false);
      }
    }, 400);
  };

  const asignarFicha = async (ficha) => {
    try {
      await api.post(`/fichas/cursos/${fichasCurso.id_curso}/${ficha.codigo}`);
      setFichasAsignadas(prev => [...prev, ficha]);
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const desasignarFicha = async (codigo) => {
    try {
      await api.delete(`/fichas/cursos/${fichasCurso.id_curso}/${codigo}`);
      setFichasAsignadas(prev => prev.filter(f => f.codigo !== codigo));
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const fichasAsignadasSet = new Set(fichasAsignadas.map(f => f.codigo));

  const generarLista = (codigoFicha) => {
    const url = `/lista-asistencia/${fichasCurso.id_curso}?ficha=${encodeURIComponent(codigoFicha)}`;
    window.open(url, "_blank");
  };

  const generarListaGeneral = (idCurso) => {
    window.open(`/lista-asistencia/${idCurso}`, "_blank");
  };

  const generarListaVersionesNuevos = (idCurso) => {
    window.open(`/lista-asistencia-versiones/${idCurso}`, "_blank");
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Courses</Typography>
        <Button variant="contained" onClick={() => { setSelectedCurso(null); setOpenForm(true); }}>
          New Course
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search by name"
          size="small"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          sx={{ flex: "2 1 200px" }}
        />
        <TextField
          select
          label="Type"
          size="small"
          value={filtroTipo}
          onChange={e => { setFiltroTipo(e.target.value); setFiltroPuesto(""); }}
          sx={{ flex: "1 1 130px" }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="general">General</MenuItem>
          <MenuItem value="puesto">By Position</MenuItem>
          <MenuItem value="opcional">Optional</MenuItem>
        </TextField>
        <TextField
          select
          label="Position"
          size="small"
          value={filtroPuesto}
          onChange={e => setFiltroPuesto(e.target.value)}
          disabled={filtroTipo === "general" || filtroTipo === "opcional"}
          sx={{ flex: "1 1 160px" }}
        >
          <MenuItem value="">All positions</MenuItem>
          {puestos.map(p => (
            <MenuItem key={p.id_puesto} value={p.id_puesto}>{p.nombre_puesto}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Name</TableCell>
            <TableCell sx={{ color: "white" }}>Type</TableCell>
            <TableCell sx={{ color: "white" }}>Start</TableCell>
            <TableCell sx={{ color: "white" }}>Validity</TableCell>
            <TableCell sx={{ color: "white" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cursosPaginados.map(curso => (
            <TableRow key={curso.id_curso} hover>
              <TableCell>{curso.nombre_curso}</TableCell>
              <TableCell>
                <Chip
                  label={TIPO_LABELS[curso.tipo] ?? curso.tipo}
                  color={TIPO_COLORS[curso.tipo] ?? "default"}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ color: curso.fecha_inicio ? "inherit" : "text.disabled", fontSize: "0.8rem" }}>
                {curso.fecha_inicio
                  ? new Date(curso.fecha_inicio + "T00:00:00").toLocaleDateString("en-US")
                  : "—"}
              </TableCell>
              <TableCell sx={{ fontSize: "0.8rem" }}>
                {curso.vigencia_meses
                  ? <Chip label={`${curso.vigencia_meses} months`} size="small" variant="outlined" />
                  : <span style={{ color: "#888" }}>Until new version</span>}
              </TableCell>
              <TableCell>
                <Tooltip title="View assigned people">
                  <IconButton onClick={() => verPersonas(curso)}>
                    <PeopleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Generate attendance sheet">
                  <IconButton onClick={() => generarListaGeneral(curso.id_curso)}>
                    <PrintOutlinedIcon />
                  </IconButton>
                </Tooltip>
                {curso.num_versiones > 1 && (
                  <Tooltip title="Print all versions (new employees only)">
                    <IconButton onClick={() => generarListaVersionesNuevos(curso.id_curso)}>
                      <HistoryEduIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Working sheets">
                  <IconButton onClick={() => abrirFichas(curso)}>
                    <ArticleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton color="primary" onClick={() => { setSelectedCurso(curso); setOpenForm(true); }}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" onClick={() => { setDeleteId(curso.id_curso); setConfirmOpen(true); }}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {cursosPaginados.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                No results
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={cursos.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Rows per page"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
      />

      {/* Dialog: assigned people */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h6" component="span">{dialogCurso?.nombre_curso}</Typography>
            <Chip
              label={TIPO_LABELS[dialogCurso?.tipo] ?? dialogCurso?.tipo}
              color={TIPO_COLORS[dialogCurso?.tipo] ?? "default"}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loadingEmpleados ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : errorEmpleados ? (
            <Alert severity="error" sx={{ m: 2 }}>{errorEmpleados}</Alert>
          ) : (
            <>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
              >
                <Tab label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Pending / To renew
                    <Chip label={pendientes.length} size="small"
                      sx={{ bgcolor: pendientes.length > 0 ? "#E65100" : "#bbb", color: "white", height: 20, fontSize: "11px" }} />
                  </Box>
                } />
                <Tab label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Completed
                    <Chip label={completados.length} size="small"
                      sx={{ bgcolor: completados.length > 0 ? "#2E7D32" : "#bbb", color: "white", height: 20, fontSize: "11px" }} />
                  </Box>
                } />
              </Tabs>

              {/* ── Tab 0: Pending / To renew ── */}
              {tab === 0 && (
                <Box sx={{ p: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#000" }}>
                        <TableCell sx={{ color: "white" }}>Name</TableCell>
                        <TableCell sx={{ color: "white" }}>Position</TableCell>
                        <TableCell sx={{ color: "white" }}>Area</TableCell>
                        <TableCell sx={{ color: "white" }} align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendientes.map(emp => (
                        <TableRow key={emp.id_empleado} hover>
                          <TableCell>{emp.nombre_completo}</TableCell>
                          <TableCell>{emp.puesto_texto}</TableCell>
                          <TableCell>{emp.area_texto}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={emp.estado === "pendiente" ? "Pending" : emp.estado === "vencido" ? "Expired" : "Outdated"}
                              size="small"
                              sx={{
                                bgcolor: emp.estado === "pendiente" ? "#E65100" : emp.estado === "vencido" ? "#C62828" : "#757575",
                                color: "white", fontWeight: 600, fontSize: "11px"
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {pendientes.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                            All employees are up to date on this course ✓
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* ── Tab 1: Completed ── */}
              {tab === 1 && (
                <Box sx={{ p: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#000" }}>
                        <TableCell sx={{ color: "white" }}>Name</TableCell>
                        <TableCell sx={{ color: "white" }}>Position</TableCell>
                        <TableCell sx={{ color: "white" }}>Area</TableCell>
                        <TableCell sx={{ color: "white" }} align="center">Version</TableCell>
                        <TableCell sx={{ color: "white" }} align="center">Date</TableCell>
                        <TableCell sx={{ color: "white" }} align="center">Expiration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {completados.map(emp => (
                        <TableRow key={emp.id_empleado} hover>
                          <TableCell>{emp.nombre_completo}</TableCell>
                          <TableCell>{emp.puesto_texto}</TableCell>
                          <TableCell>{emp.area_texto}</TableCell>
                          <TableCell align="center">
                            <Chip label={`v${emp.numero_version ?? "—"}`} size="small" color="success" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">{emp.fecha_capacitacion ?? "—"}</TableCell>
                          <TableCell align="center">
                            {emp.fecha_vencimiento
                              ? emp.fecha_vencimiento
                              : emp.estado === "desactualizado"
                                ? <Chip label="Outdated" size="small"
                                    sx={{ bgcolor: "#757575", color: "white", fontSize: "11px" }} />
                                : <span style={{ color: "#888", fontStyle: "italic" }}>Until new version</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                      {completados.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                            No employee has completed this course yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <CursoForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        curso={selectedCurso}
        onSaved={() => cargarCursos(busqueda, filtroTipo, filtroPuesto)}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete course?"
        onConfirm={eliminarCurso}
      />

      {/* Dialog: working sheets */}
      <Dialog open={fichasOpen} onClose={() => setFichasOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ArticleIcon fontSize="small" />
            Working sheets
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {fichasCurso?.nombre_curso}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Section: assigned working sheets */}
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Assigned ({fichasAsignadas.length})
            </Typography>
            {loadingFichas ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : fichasAsignadas.length === 0 ? (
              <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No working sheets assigned
                </Typography>
                <Tooltip title="Generate attendance sheet without a working sheet">
                  <Button
                    size="small" variant="outlined" startIcon={<PrintIcon />}
                    onClick={() => generarListaGeneral(fichasCurso.id_curso)}
                  >
                    General attendance sheet
                  </Button>
                </Tooltip>
              </Box>
            ) : (
              <List dense disablePadding sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                {fichasAsignadas.map((f, idx) => (
                  <ListItem
                    key={f.codigo}
                    divider={idx < fichasAsignadas.length - 1}
                    sx={{ py: 0.5 }}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        {f.archivo_url && (
                          <Chip
                            icon={<PictureAsPdfIcon />}
                            label="View PDF"
                            size="small"
                            color="error"
                            variant="outlined"
                            clickable
                            component="a"
                            href={`${baseURL}/${f.archivo_url.replace(/^fichas\//, "fichas_archivos/")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ mr: 1 }}
                          />
                        )}
                        <Tooltip title="Generate attendance sheet">
                          <IconButton size="small" color="primary" onClick={() => generarLista(f.codigo)}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {hasPermission("CURSOS_EDITAR") && (
                          <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => desasignarFicha(f.codigo)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={f.codigo}
                      secondary={f.nombre}
                      primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {hasPermission("CURSOS_EDITAR") && (
            <>
              <Divider sx={{ my: 1 }} />
              {/* Section: search and add */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Search and add working sheet
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type code or name (min. 2 characters)…"
                  value={fichasBusqueda}
                  onChange={e => handleFichasBusqueda(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {loadingBusqueda ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />

                {fichasResultados.length > 0 && (
                  <List dense disablePadding sx={{ maxHeight: 260, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    {fichasResultados.map(f => (
                      <ListItem key={f.codigo} divider sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={f.codigo}
                          secondary={f.nombre}
                          primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                        <ListItemSecondaryAction>
                          {fichasAsignadasSet.has(f.codigo) ? (
                            <Chip label="Assigned" size="small" color="success" variant="outlined" />
                          ) : (
                            <Tooltip title="Add">
                              <IconButton size="small" color="primary" onClick={() => asignarFicha(f)}>
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}

                {fichasBusqueda.trim().length >= 2 && !loadingBusqueda && fichasResultados.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No results for "{fichasBusqueda}"
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setFichasOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
